import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VERSIONS = [
  { key: 'Homeowner', label: 'Homeowner' },
  { key: 'Investor', label: 'Investor' },
  { key: 'Builder', label: 'Builder' },
  { key: 'Architect', label: 'Architect' },
  { key: null, label: 'Role not given' },
];

export function WelcomeEmailAdmin({ token }) {
  const [state, setState] = useState(null);
  const [to, setTo] = useState('bm@macventure.co.uk');
  const [picked, setPicked] = useState(VERSIONS.map((v) => v.label));
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const cfg = { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true };

  const load = useCallback(async () => {
    try { setState((await axios.get(`${API_URL}/api/admin/welcome-email`, cfg)).data); } catch (err) { setError(err?.response?.data?.error || 'Failed to load'); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const toggle = async (enabled) => {
    const msg = enabled
      ? 'Switch ON the new welcome email? New sign-ups will get it after step 2 (or 15 minutes after step 1).'
      : 'Switch OFF the new welcome email? New sign-ups will get the old confirmation straight away.';
    if (!window.confirm(msg)) return;
    try { await axios.put(`${API_URL}/api/admin/welcome-email`, { enabled }, cfg); load(); } catch (err) { setError(err?.response?.data?.error || 'Failed'); }
  };

  const sendTests = async () => {
    setBusy(true);
    setError('');
    setResults(null);
    try {
      const roles = VERSIONS.filter((v) => picked.includes(v.label)).map((v) => v.key);
      setResults((await axios.post(`${API_URL}/api/admin/welcome-email-test`, { to, roles }, cfg)).data.results);
    } catch (err) {
      setError(err?.response?.data?.error || 'Test failed');
    } finally {
      setBusy(false);
    }
  };

  const flip = (label) => setPicked((p) => (p.includes(label) ? p.filter((x) => x !== label) : [...p, label]));
  const on = !!state?.enabled;
  return (
    <div className="card p-6 mb-6" data-testid="welcome-email-card">
      <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-muted)' }}>Welcome email (new version)</div>
      <div className="flex flex-wrap items-center gap-4 mb-2">
        <div className="text-lg font-bold" style={{ color: on ? '#166534' : 'var(--color-navy)' }} data-testid="welcome-email-status">{on ? 'ON' : 'OFF (old confirmation in use)'}</div>
        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--color-border)' }}>
          {[['off', false], ['on', true]].map(([label, val]) => (
            <button key={label} disabled={!state || on === val} onClick={() => toggle(val)} className="px-4 py-2 text-sm font-medium uppercase"
              style={{ background: on === val ? 'var(--color-navy)' : 'white', color: on === val ? 'white' : 'var(--color-muted)' }}
              data-testid={`welcome-email-${label}-btn`}>{label}</button>
          ))}
        </div>
      </div>
      {state?.updated_at && <p className="text-xs mb-3" style={{ color: 'var(--color-muted)' }}>Last changed {new Date(state.updated_at).toLocaleString('en-GB')} by {state.updated_by}</p>}
      <p className="text-sm mb-4" style={{ color: 'var(--color-muted)' }}>"Welcome to SchedulesOfWork", with a block of text that matches the member's role. Sent once per member, when they finish or skip step 2, or 15 minutes after step 1.</p>

      <div className="text-sm font-semibold mb-2" style={{ color: 'var(--color-navy)' }}>Send test versions</div>
      <div className="flex flex-wrap gap-3 mb-3">
        {VERSIONS.map((v) => (
          <label key={v.label} className="flex items-center gap-1 text-sm">
            <input type="checkbox" checked={picked.includes(v.label)} onChange={() => flip(v.label)} data-testid={`welcome-test-version-${v.label.toLowerCase().replace(/ /g, '-')}`} /> {v.label}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <input className="input-styled" style={{ minWidth: 280 }} type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="welcome-test-to-input" />
        <button className="btn-primary px-4 py-2" onClick={sendTests} disabled={busy || !picked.length} data-testid="welcome-test-send-btn">
          {busy ? 'Sending…' : `Send ${picked.length} test email${picked.length === 1 ? '' : 's'}`}
        </button>
      </div>
      {error && <div className="p-3 mt-4 rounded-lg text-sm" style={{ background: '#FEE2E2', color: '#B91C1C' }}>{error}</div>}
      {results && (
        <div className="mt-4 p-4 rounded-lg space-y-1 text-sm" style={{ background: 'var(--color-bg)' }} data-testid="welcome-test-results">
          {results.map((r) => (
            <div key={r.version} style={{ color: r.delivered ? '#166534' : '#B91C1C' }}>
              <strong>{r.version}:</strong> {r.delivered ? `delivered via ${r.via === 'gmail' ? 'Gmail' : 'Resend'}` : `failed (${r.resend_error || r.gmail_error})`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
